/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Monster from './model/Monster';
import {
  processText,
  sanitizeMarkdownInput,
  replaceAbilityTags
} from './services/MarkdownToHtml';

interface MarkdownTextProps {
  text: string;
  mon: Monster | null;
}

const MarkdownText: React.FC<MarkdownTextProps> = ({ text, mon }) => {
  const sections = processText(text);

  const content = sections.map((line, index) => {
    const sanitized = sanitizeMarkdownInput(line);
    const finalText = mon ? replaceAbilityTags(sanitized, mon) : sanitized;

    const markdown = (
      <ReactMarkdown
        key={`md-${index}`}
        remarkPlugins={[remarkGfm]}
        // Disable dangerous HTML elements
        disallowedElements={['script', 'iframe', 'img']}
        unwrapDisallowed={true}
        components={{
          p: ({ node, ...props }) => <span {...props} />, // Convert <p> tags to <span> to prevent nesting issues
          table: ({ node, ...props }) => <table className='markdown-table' {...props} />,
          a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />
        }}
      >
        {finalText}
      </ReactMarkdown>
    );

    if (index === 0) {
      // We want the first line to be a <span> so it shows on the same line as the header
      return <span key={`span-${index}`}>{markdown}</span>;
    }

    // But anything after the first line needs to be on its own line, so we use <section> elements
    return <section key={`section-${index}`}>{markdown}</section>;
  });

  return <span className='markdown'>{content}</span>;
};

export default MarkdownText;